import React, { useCallback, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import PaletteSelectablePill from 'web-palette/dist/components/SelectablePill/SelectablePill';

import { spreadProps } from '@ulta/core/utils/dom/dom';

import * as utils from './SelectablePill';

/**
  * Represents an SelectablePill component
  * @method
  * @param {SelectablePillProps} props - React properties passed from composition
  * @returns PalSelectablePill
  */

const SelectablePill = ( props ) => {
  const {
    onClick,
    selectAction
  } = props;

  const handleOnClick = useCallback( () => {
    onClick( selectAction );
  }, [selectAction, onClick] );

  const PalSelectablePill = PaletteSelectablePill.default || PaletteSelectablePill;
  const selectablePillRef = useRef( null );

  const selectablePillProps = utils.prepareSelectablePillProps( { props }, { handleOnClick } );

  return (
    <div
      className='SelectablePill'
      ref={ selectablePillRef }
    >
      <PalSelectablePill
        { ...spreadProps( { ...selectablePillProps } ) }
      />
    </div>
  );
};

/**
 * prepareSelectablePillProps is a function that prepares properties for the PaletteSelectablePill component.
 * @method
 * @param {object} data - An object containing props.
 * @param {object} methods - An object containing handler functions.
 * @returns {object} - Returns an object with prepared properties for the PaletteSelectablePill component.
 */

export const prepareSelectablePillProps = ( data, methods ) => {
  const { props } = data || {};
  const { handleOnClick } = methods || {};
  let actionType = 'button';

  if( !props ){
    return {};
  }

  const {
    onClick,
    selectAction,
    linkSelectAction,
    ...paletteProps
  } = props;

  if( linkSelectAction?.url ){
    actionType = 'link';
  }

  return {
    ...paletteProps,
    actionType,
    externalPillAction: handleOnClick,
    ...( linkSelectAction?.url && { href: linkSelectAction.url } )
  };
};

/**
 * Property type definitions
 * @typedef SelectablePillProps
 * @type {object}
 * @property {func} onClick - Sets the onClick function
 * @property {any} selectAction - Sets the selectAction for the pill
 * @property {string} actionType - Sets the type of action ( button or link )
 * @property {boolean} active - Sets the selected state for button/link checked state
 * @property {string} ariaDescribedBy - sets the aria-describedby attribute for button or input elements
 * @property {boolean} disabled - Sets the disabled state
 * @property {string} dismissibleIcon - Sets the dismissible icon for the active filter pill
 * @property {func} externalPillAction - Sets the onClick for button or link
 * @property {string} iconName - Sets the icon for the pill
 * @property {string} message - Sets the message to be displayed in the pill
 * @property {any} ref - Sets the ref of the selectable card
 * @property {string} screenReaderText - Sets the screenReaderText for the pill
 * @property {boolean} unavailable - Sets the unavailable state for button/link checked state
 * @property {string} variant - Sets the variant of the SelectablePill
 */

export const propTypes =  {
  /**
   * Sets the onClick function
   */
  onClick: PropTypes.func,
  /**
   * Sets the selectAction for the pill
   */
  selectAction: PropTypes.any,
  /**
   * Sets the type of action ( button or link )
   */
  actionType: PropTypes.oneOf( ['button', 'link'] ),
  /**
   * Sets the active state for button/link checked state
   */
  active: PropTypes.bool,
  /**
   * Sets the aria-describedby attribute for button or input elements
   */
  ariaDescribedBy: PropTypes.string,
  /**
   * Sets the disabled state
   */
  disabled: PropTypes.bool,
  /**
   * Sets the dismissible icon for the active filter pill
   */
  dismissibleIcon: PropTypes.string,
  /**
   * Sets the onClick for button or link
   */
  externalPillAction: PropTypes.func,
  /**
   * Sets the icon for the pill
   */
  iconName: PropTypes.string,
  /**
   * Sets the message to be displayed in the pill
   */
  message: PropTypes.string,
  /**
   * pill reference
   */
  ref: PropTypes.any,
  /**
   * Sets the screenReaderText for the pill
   */
  screenReaderText: PropTypes.string,
  /**
   * Sets the unavailable state for button/link checked state
   */
  unavailable: PropTypes.bool,
  /**
   * Sets the variant of the SelectablePill
   */
  variant: PropTypes.oneOf( ['default', 'filter'] )
};

SelectablePill.propTypes = propTypes;

export default SelectablePill;